import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useUserSettingsDataContext} from "../../../../../../../providers/UserSettingsProvider";
import {WallDeduction} from "../../../models/editor";
import {TopViewWallAreaHelper} from "../../topViewWallArea/TopViewWallAreaHelper";
import {convertToMeters, convertToUnit, squareMetersToSquareFeet} from "../../../../../../../utils/ConversionUtils";
import {formatArea} from "../../../../../../../utils/TextUtils";
import {UnitsType} from "../../../../../../../models/enums";
import EditValueModal from "../../../../../../../components/modal/EditValueModal";
import LengthInput from "../../../../../../../components/ui/LengthInput";
import NumberInput from "../../../../../../../components/ui/NumberInput";
import "./createWallDeductionModal.scss";

interface Props {
	isOpen: boolean;
	onSave: (deduction: Omit<WallDeduction, 'id'>) => void;
	onClose: () => void;
	existingDeduction?: WallDeduction;
}

type DeductionFields = {
	leftHeight: number | undefined;
	rightHeight: number | undefined;
	topWidth: number | undefined;
	bottomWidth: number | undefined;
};

const emptyFields: DeductionFields = {
	leftHeight: undefined,
	rightHeight: undefined,
	topWidth: undefined,
	bottomWidth: undefined,
};

function isManualAreaDeduction(d: WallDeduction): boolean {
	return d.leftHeight === 0 && d.rightHeight === 0 && d.topWidth === 0 && d.bottomWidth === 0 && d.area > 0;
}

const CreateWallDeductionModal: React.FC<Props> = ({isOpen, onSave, onClose, existingDeduction}) => {
	const {t} = useTranslation();
	const {settings} = useUserSettingsDataContext();
	const [fields, setFields] = useState<DeductionFields>(emptyFields);
	const [manualArea, setManualArea] = useState<number | undefined>(undefined);
	const [measurementsOpen, setMeasurementsOpen] = useState(false);

	useEffect(() => {
		if (!isOpen) return;
		if (existingDeduction) {
			if (settings?.measurementSystem === undefined) return;
			if (isManualAreaDeduction(existingDeduction)) {
				const displayArea = settings.measurementSystem === UnitsType.IMPERIAL
					? squareMetersToSquareFeet(existingDeduction.area)
					: existingDeduction.area;
				setManualArea(displayArea);
				setFields(emptyFields);
				setMeasurementsOpen(false);
			} else {
				const toDisplay = (v: number) => v === 0 ? 0 : convertToUnit(v, settings.measurementSystem);
				setFields({
					leftHeight: toDisplay(existingDeduction.leftHeight),
					rightHeight: toDisplay(existingDeduction.rightHeight),
					topWidth: toDisplay(existingDeduction.topWidth),
					bottomWidth: toDisplay(existingDeduction.bottomWidth),
				});
				setManualArea(undefined);
				setMeasurementsOpen(true);
			}
		} else {
			setFields(emptyFields);
			setManualArea(undefined);
			setMeasurementsOpen(false);
		}
	}, [isOpen, existingDeduction, settings?.measurementSystem]);

	const measurementSystem = settings?.measurementSystem;

	const anyMeasurementFilled =
		fields.leftHeight !== undefined ||
		fields.rightHeight !== undefined ||
		fields.topWidth !== undefined ||
		fields.bottomWidth !== undefined;

	const allMeasurementsFilled =
		measurementSystem !== undefined &&
		fields.leftHeight !== undefined &&
		fields.rightHeight !== undefined &&
		fields.topWidth !== undefined &&
		fields.bottomWidth !== undefined;

	const manualAreaDisabled = anyMeasurementFilled;
	const measurementsDisabled = manualArea !== undefined;

	const canSave = manualArea !== undefined || allMeasurementsFilled;

	const toMeters = (v: number) => v === 0 ? 0 : (convertToMeters(v, measurementSystem) ?? v);

	const previewAreaInM2 = (() => {
		if (manualArea !== undefined && measurementSystem !== undefined) {
			return measurementSystem === UnitsType.IMPERIAL
				? manualArea / squareMetersToSquareFeet(1)
				: manualArea;
		}
		if (allMeasurementsFilled) {
			return TopViewWallAreaHelper.calculateDeductionArea({
				leftHeight: toMeters(fields.leftHeight!),
				rightHeight: toMeters(fields.rightHeight!),
				topWidth: toMeters(fields.topWidth!),
				bottomWidth: toMeters(fields.bottomWidth!),
			});
		}
		return undefined;
	})();

	const handleChange = (field: keyof DeductionFields) => (value: number | undefined) => {
		setFields(prev => ({...prev, [field]: value}));
	};

	const handleSave = () => {
		if (manualArea !== undefined && measurementSystem !== undefined) {
			const areaInM2 = measurementSystem === UnitsType.IMPERIAL
				? manualArea / squareMetersToSquareFeet(1)
				: manualArea;
			onSave({leftHeight: 0, rightHeight: 0, topWidth: 0, bottomWidth: 0, area: areaInM2});
			return;
		}
		if (!allMeasurementsFilled || measurementSystem === undefined) return;
		const leftHeight = toMeters(fields.leftHeight!);
		const rightHeight = toMeters(fields.rightHeight!);
		const topWidth = toMeters(fields.topWidth!);
		const bottomWidth = toMeters(fields.bottomWidth!);
		const area = TopViewWallAreaHelper.calculateDeductionArea({leftHeight, rightHeight, topWidth, bottomWidth});
		onSave({leftHeight, rightHeight, topWidth, bottomWidth, area});
	};

	const unitSuffix = measurementSystem === UnitsType.METRIC ? ` [${t("common.units.meterSymbol")}]` : "";
	const areaUnitSuffix = measurementSystem === UnitsType.METRIC
		? ` [m${String.fromCharCode(178)}]`
		: ` [ft${String.fromCharCode(178)}]`;

	const isEditMode = !!existingDeduction;
	const title = isEditMode
		? t("editor.topViewWallArea.createDeductionModal.editTitle")
		: t("editor.topViewWallArea.createDeductionModal.title");

	return (
		<EditValueModal
			isOpen={isOpen}
			onClose={onClose}
			onSave={handleSave}
			title={title}
			disableSave={!canSave}
		>
			<div className="create-wall-deduction-modal-fields">
				<div className={manualAreaDisabled ? "is-disabled" : undefined}>
					<NumberInput
						label={`${t("editor.topViewWallArea.createDeductionModal.areaInput")}${areaUnitSuffix}`}
						value={manualArea}
						onChange={setManualArea}
						min={0}
					/>
				</div>
				<div className="create-wall-deduction-modal-separator">
					<span>{t("editor.topViewWallArea.createDeductionModal.or")}</span>
				</div>
				<div className={measurementsDisabled ? "create-wall-deduction-modal-measurements is-disabled" : "create-wall-deduction-modal-measurements"}>
					<button
						className="create-wall-deduction-modal-measurements-toggle"
						onClick={() => setMeasurementsOpen(prev => !prev)}
						type="button"
					>
						<span>{t("editor.topViewWallArea.createDeductionModal.enterMeasurements")}</span>
						<span className="create-wall-deduction-modal-measurements-chevron">{measurementsOpen ? "▲" : "▼"}</span>
					</button>
					{measurementsOpen && (
						<div className="create-wall-deduction-modal-measurements-fields">
							<LengthInput
								label={`${t("editor.topViewWallArea.createDeductionModal.leftHeight")}${unitSuffix}`}
								value={fields.leftHeight}
								unitsType={measurementSystem}
								onChange={handleChange("leftHeight")}
							/>
							<LengthInput
								label={`${t("editor.topViewWallArea.createDeductionModal.rightHeight")}${unitSuffix}`}
								value={fields.rightHeight}
								unitsType={measurementSystem}
								onChange={handleChange("rightHeight")}
							/>
							<LengthInput
								label={`${t("editor.topViewWallArea.createDeductionModal.topWidth")}${unitSuffix}`}
								value={fields.topWidth}
								unitsType={measurementSystem}
								onChange={handleChange("topWidth")}
							/>
							<LengthInput
								label={`${t("editor.topViewWallArea.createDeductionModal.bottomWidth")}${unitSuffix}`}
								value={fields.bottomWidth}
								unitsType={measurementSystem}
								onChange={handleChange("bottomWidth")}
							/>
						</div>
					)}
				</div>
			</div>
			<div style={{margin: "8px auto 0", textAlign: "center"}}>
				{t("editor.topViewWallArea.createDeductionModal.totalDeduction", {
					value: formatArea(previewAreaInM2, measurementSystem),
				})}
			</div>
		</EditValueModal>
	);
};

export default CreateWallDeductionModal;
